import React, { useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Icon from '@components/icon/Icon';
import { mdiArrowUp, mdiArrowDown } from '@mdi/js';
import { Table, Checkbox } from '@trussworks/react-uswds';

import Tooltip from '@components/tooltip/tooltip';
import HeaderCell from '../tableCellComponents/HeaderCell/HeaderCell';

import './TanStackTable.scss';
import EmptyTableRow from '../EmptyTableRow';
import classNames from 'classnames';
import Pagination from '@components/pagination/Pagination';

const TanStackTableNew = ({
  addMultipleRows,
  addRow,
  columns,
  data,
  headerCellContent,
  hideDrag = true,
  ignoredHeaders,
  initialTableState,
  isInputTable = false,
  isReadOnly,
  placeholderClick,
  placeholderText,
  removeMultipleRows,
  rowErrorCallback,
  tableVersion,
  updateSourceData,
  validationSchema,
}) => {
  const [rowErrors, setRowErrors] = useState();

  useEffect(() => {
    const hasErrors = (rowErrors && Object?.keys(rowErrors)?.length > 0) ?? false;
    rowErrorCallback(() => hasErrors);
  }, [rowErrorCallback, rowErrors]);

  const debounce = useCallback((func, wait) => {
    let timeout;

    const executedFunction = function (...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };

      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };

    executedFunction.cancel = function () {
      clearTimeout(timeout);
    };

    return executedFunction;
  }, []);

  const debouncedValidation = useCallback(
    () =>
      debounce((rows) => {
        rows.forEach(({ rowIndex, updatedRowData }) => {
          validationSchema
            .validate(updatedRowData, { abortEarly: false })
            .then(() => {
              setRowErrors((currentErrors) => {
                const newErrors = { ...currentErrors };
                delete newErrors[rowIndex];
                return newErrors;
              });
            })
            .catch((err) => {
              if (err.inner && Array.isArray(err.inner)) {
                const newRowErrors = err.inner.reduce((acc, currError) => {
                  acc[currError.path] = currError.message;
                  return acc;
                }, {});

                setRowErrors((currentErrors) => ({
                  ...currentErrors,
                  [rowIndex]: newRowErrors,
                }));
              } else {
                console.error('Validation error:', err.message);
              }
            });
        });
      }, 500),
    [validationSchema, debounce]
  );

  useEffect(() => {
    if (data) {
      const rowsToValidate = data.map((feature, index) => ({
        rowIndex: index,
        updatedRowData: feature,
      }));
      const validateRows = debouncedValidation();
      validateRows(rowsToValidate);
    }
  }, [data, debouncedValidation]);

  const [sorting, setSorting] = useState([]);
  const [editedRows, setEditedRows] = useState({});

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    manualPagination: false,
    enableRowSelection: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    autoResetPageIndex: false,
    initialState: {
      ...initialTableState,
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting,
    },
    meta: {
      editedRows,
      tableVersion,
      setEditedRows,
      updateData: (rowIndex, columnId, value) => {
        updateSourceData(rowIndex, columnId, value);
        const updatedRowData = { ...data[rowIndex], [columnId]: value };
        debouncedValidation([{ rowIndex, updatedRowData }]);
      },
      addRow: () => {
        addRow();
      },
      addRowsFromFile: (newRows) => {
        addMultipleRows(newRows);
      },
      removeSelectedRows: (selectedRows) => {
        const confirmed = confirm(
          `Are you sure you want to delete ${selectedRows?.length} rows? This action is permanent and cannot be undone!`
        );
        confirmed && removeMultipleRows(selectedRows);
        confirmed && table.resetRowSelection();
      },
    },
  });

  const tableStyles = classNames({
    'input-table': isInputTable,
  });

  return (
    <>
      <HeaderCell table={table} isReadOnly={isReadOnly} hideDrag={hideDrag} ignoredHeaders={ignoredHeaders} />
      {headerCellContent && <div className='table-header-info'>{headerCellContent}</div>}
      <div className='table-container'>
        <Table bordered className={tableStyles} fullWidth>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={isReadOnly ? header.column.getToggleSortingHandler() : null}
                    colSpan={header.colSpan}
                    style={{
                      position: 'relative',
                      width: header.getSize() + 'px',
                      cursor: 'col-resize',
                      userSelect: header.column.getIsResizing() ? 'none' : 'auto',
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          style={{
                            cursor: header.column.getIsResizing() ? 'col-resize' : isReadOnly ? 'pointer' : 'auto',
                            position: 'relative',
                          }}
                        >
                          {/* Render "Select All" toggle for selection column */}
                          {header.id === 'edit' && !isReadOnly ? (
                            <Checkbox
                              className='box-shadow-1px'
                              name='edit'
                              title='Select All Rows'
                              label=''
                              id={`${uuidv4()}_edit`}
                              checked={table.getIsAllRowsSelected()}
                              onChange={(e) => table.toggleAllRowsSelected(e.target.checked)}
                            />
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}

                          {/* Tooltip and other meta elements */}
                          {header.column.columnDef.meta?.tooltip && (
                            <Tooltip
                              header={header.column.columnDef.header}
                              content={header.column.columnDef.meta?.tooltip}
                            />
                          )}
                          {header.column.columnDef.meta?.required && <span className='red-asterisk'>*</span>}

                          {/* Sorting Icons */}
                          {
                            {
                              asc: <Icon path={mdiArrowUp} size='16px' />,
                              desc: <Icon path={mdiArrowDown} size='16px' />,
                            }[header.column.getIsSorted() ?? null]
                          }
                        </div>

                        {/* Add resizer element */}
                        <div
                          className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          style={{ padding: '0 5px', cursor: 'col-resize' }}
                        />
                      </>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel()?.rows?.length === 0 && (
              <EmptyTableRow onAddClick={placeholderClick} placeholderText={placeholderText} isReadOnly={isReadOnly} />
            )}
            {table.getRowModel()?.rows?.map((row) => (
              <tr
                key={row.id}
                className={`${row.getIsSelected() ? 'selected-row' : ''} ${rowErrors && rowErrors?.[row.id] && Object.keys(rowErrors[row.id])?.length !== 0 ? 'row-error' : ''}`}
              >
                {row.getVisibleCells().map((cell) => {
                  const cellError = rowErrors?.[row.id]?.[cell.column.id];
                  return (
                    <td key={cell.id} style={{ width: cell.column.getSize() + 'px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {cellError && (
                          <Tooltip
                            iconSize='large'
                            place='bottom'
                            header={cell.column.columnDef.header}
                            content={cellError}
                            iconStyle={{ color: 'red' }}
                            isError
                            noDelay
                            border
                          />
                        )}
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Pagination
        currentPage={table.getState().pagination.pageIndex + 1}
        itemsPerPage={table.getState().pagination.pageSize}
        onPageChange={(pageNumber) => table.setPageIndex(pageNumber - 1)}
        onPageSizeChange={(pageSize) => table.setPageSize(pageSize)}
        pageCount={table.getPageCount()}
      />
    </>
  );
};

export default TanStackTableNew;
